import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  HowToReg as DraftIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import unifiedApi from '../../../utils/unifiedApi';

const MIN_SEARCH_LENGTH = 2;

const getName = (member) => `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email;

const DraftViewersManager = ({ leagueId, leagueSlug }) => {
  const [viewers, setViewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const loadViewers = useCallback(async () => {
    try {
      const data = await unifiedApi.getLeagueDraftViewers(leagueId);
      setViewers(data || []);
    } catch (err) {
      setError('Failed to load draft viewers.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    loadViewers();
  }, [loadViewers]);

  useEffect(() => {
    const term = searchInput.trim();
    if (selected) return undefined;
    if (term.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await unifiedApi.searchLeagueDraftViewerCandidates(leagueId, term);
        if (!cancelled) setOptions(results || []);
      } catch (err) {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchInput, leagueId, selected]);

  const handleAdd = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await unifiedApi.addLeagueDraftViewer(leagueId, selected.userTenantId);
      setSuccess(`${getName(selected)} can now view the draft.`);
      setSelected(null);
      setSearchInput('');
      setOptions([]);
      await loadViewers();
    } catch (err) {
      setError(err.message || 'Failed to add draft viewer.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (viewer) => {
    setRemovingId(viewer.userTenantId);
    setError(null);
    setSuccess(null);
    try {
      await unifiedApi.removeLeagueDraftViewer(leagueId, viewer.userTenantId);
      setSuccess(`Removed draft access for ${getName(viewer)}.`);
      await loadViewers();
    } catch (err) {
      setError(err.message || 'Failed to remove draft viewer.');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        League managers, reporters, and organization admins can always see the draft.
        Grant other members access to this league&apos;s draft here.
      </Typography>

      {leagueSlug && (
        <Button
          variant="outlined"
          component={Link}
          to={`/leagues/${leagueSlug}/draft`}
          startIcon={<DraftIcon />}
          fullWidth
          sx={{ mb: 2 }}
        >
          Open Draft Board
        </Button>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <Autocomplete
          fullWidth
          size="small"
          options={options}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          inputValue={searchInput}
          clearOnBlur={false}
          onInputChange={(_, value, reason) => {
            setSearchInput(value);
            // Typing or clearing after a pick must not leave the old member selected.
            if (reason !== 'reset') setSelected(null);
          }}
          filterOptions={(x) => x}
          getOptionLabel={(option) => `${getName(option)} (${option.email})`}
          isOptionEqualToValue={(option, value) => option.userTenantId === value.userTenantId}
          loading={searching}
          noOptionsText={searchInput.trim().length < MIN_SEARCH_LENGTH ? 'Type at least 2 characters' : 'No members found'}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search members by name or email"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searching ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!selected || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
          sx={{ flexShrink: 0 }}
        >
          Grant Access
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : viewers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No draft viewers have been added.</Typography>
      ) : (
        <List dense disablePadding aria-label="Draft viewers">
          {viewers.map(viewer => (
            <ListItem
              key={viewer.userTenantId}
              divider
              secondaryAction={(
                <Tooltip title="Remove draft access">
                  <span>
                    <IconButton
                      edge="end"
                      aria-label={`Remove draft access for ${getName(viewer)}`}
                      onClick={() => handleRemove(viewer)}
                      disabled={removingId === viewer.userTenantId}
                    >
                      {removingId === viewer.userTenantId ? <CircularProgress size={18} /> : <DeleteIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            >
              <ListItemText
                primary={(
                  <Box
                    component={Link}
                    to={`/player/${viewer.userTenantId}`}
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {getName(viewer)}
                  </Box>
                )}
                secondary={viewer.email}
                secondaryTypographyProps={{ sx: { wordBreak: 'break-all' } }}
                sx={{ pr: 4 }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default DraftViewersManager;
